import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryClient, withClientId } from "@/lib/queryClient";

import {
  createInvoice,
  createTransaction,
  deleteTransaction,
  fetchFloorplanSummary,
  fetchInvoice,
  fetchInvoices,
  fetchPnL,
  fetchStorageInvoicingStatus,
  fetchTransactions,
  fetchTrend,
  openInvoice,
  recordPayment,
  updateInvoiceDraft,
  updateTransaction,
  voidInvoice,
  type InvoiceUpdateBody,
  type PaymentRecordBody,
  type TransactionFilters,
  type TransactionWriteBody,
} from "./api";
import type { Transaction } from "@/types/finance";
import { FLOORPLAN_SUMMARY_KEY } from "@/features/inventory/hooks";

// Finance query keys. The P&L is keyed on the period so switching months
// refetches rather than serving stale statements; mutations invalidate by
// prefix (["pnl"], ["transactions"]) to cover every period at once.
export const PNL_KEY = ["pnl"] as const;
export const TREND_KEY = ["finance-trend"] as const;
export const TRANSACTIONS_KEY = ["transactions"] as const;

export const pnlKey = (from: string, to: string) => ["pnl", { from, to }] as const;

export const usePnL = (from: string, to: string) =>
  useQuery({
    queryKey: pnlKey(from, to),
    queryFn: () => fetchPnL(from, to),
    enabled: !!from && !!to,
  });

export const useTrend = (months = 12) =>
  useQuery({
    queryKey: [...TREND_KEY, months],
    queryFn: () => fetchTrend(months),
  });

// Floor-plan exposure lives under the inventory loan mutations' invalidation
// set (FLOORPLAN_SUMMARY_KEY, owned by features/inventory/hooks) — paying or
// editing a curtailment refreshes it from there.
export const useFloorplanSummary = () =>
  useQuery({
    queryKey: FLOORPLAN_SUMMARY_KEY,
    queryFn: fetchFloorplanSummary,
  });

export const transactionsKey = (filters: TransactionFilters) => ["transactions", filters];

export const useTransactions = (filters: TransactionFilters) =>
  useQuery({
    queryKey: transactionsKey(filters),
    queryFn: () => fetchTransactions(filters),
  });

const invalidateFinance = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY });
  qc.invalidateQueries({ queryKey: PNL_KEY });
  qc.invalidateQueries({ queryKey: TREND_KEY });
};

// Outbox keys (see apps/frontend/AGENTS.md "offline outbox"): paused offline
// mutations replay through the setMutationDefaults registrations below.
export const TRANSACTION_CREATE_KEY = ["outbox", "transactions", "create"] as const;
export const TRANSACTION_UPDATE_KEY = ["outbox", "transactions", "update"] as const;
export const TRANSACTION_DELETE_KEY = ["outbox", "transactions", "delete"] as const;

queryClient.setMutationDefaults(TRANSACTION_CREATE_KEY, { mutationFn: createTransaction });
queryClient.setMutationDefaults(TRANSACTION_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: TransactionWriteBody }) =>
    updateTransaction(vars.id, vars.body),
});
queryClient.setMutationDefaults(TRANSACTION_DELETE_KEY, { mutationFn: deleteTransaction });

export const useCreateTransaction = () => {
  const qc = useQueryClient();
  const mutation = useMutation<Transaction, Error, TransactionWriteBody & { id: string }>({
    mutationKey: TRANSACTION_CREATE_KEY,
    mutationFn: createTransaction,
    onSuccess: () => invalidateFinance(qc),
  });
  return {
    ...mutation,
    mutate: (body: TransactionWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: TransactionWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  };
};

export const useUpdateTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: TRANSACTION_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: TransactionWriteBody }) =>
      updateTransaction(vars.id, vars.body),
    onSuccess: () => invalidateFinance(qc),
  });
};

export const useDeleteTransaction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: TRANSACTION_DELETE_KEY,
    mutationFn: (id: string) => deleteTransaction(id),
    onSuccess: () => invalidateFinance(qc),
  });
};

// ---- Accounts receivable ----

export const INVOICES_KEY = ["invoices"] as const;
export const invoiceKey = (id: string) => ["invoices", id] as const;
export const STORAGE_INVOICING_KEY = ["storage-invoicing-status"] as const;

export const useInvoices = (filters: { status?: string; q?: string; source?: string }) =>
  useQuery({
    queryKey: ["invoices", filters],
    queryFn: () => fetchInvoices(filters),
  });

export const useInvoice = (id: string) =>
  useQuery({ queryKey: invoiceKey(id), queryFn: () => fetchInvoice(id), enabled: !!id });

export const useStorageInvoicingStatus = () =>
  useQuery({ queryKey: STORAGE_INVOICING_KEY, queryFn: fetchStorageInvoicingStatus });

const invalidateInvoices = (qc: ReturnType<typeof useQueryClient>, id?: string) => {
  void qc.invalidateQueries({ queryKey: INVOICES_KEY });
  void qc.invalidateQueries({ queryKey: STORAGE_INVOICING_KEY });
  if (id) void qc.invalidateQueries({ queryKey: invoiceKey(id) });
};

export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInvoice,
    onSuccess: (_inv) => invalidateInvoices(qc),
  });
};

export const useUpdateInvoiceDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InvoiceUpdateBody }) =>
      updateInvoiceDraft(id, body),
    onSuccess: (inv) => invalidateInvoices(qc, inv.id),
  });
};

export const useOpenInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: openInvoice,
    onSuccess: (inv) => invalidateInvoices(qc, inv.id),
  });
};

export const useVoidInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: voidInvoice,
    onSuccess: (inv) => invalidateInvoices(qc, inv.id),
  });
};

export const useRecordPayment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PaymentRecordBody }) => recordPayment(id, body),
    onSuccess: (result) => invalidateInvoices(qc, result.invoice.id),
  });
};
